import { Fragment, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useOrgData } from '../hooks/useOrgData';
import { ROLE_LABELS, isAdminOrGestor } from '../lib/rbac';
import { listUsers, type AdminUserRow } from '../lib/admin';
import { loadManagers } from '../lib/team';
import { profileFor } from '../lib/profiles';
import { pendingFor, type PendingItem, type Recipient } from '../shared/pendencias';

interface Linha {
  u: AdminUserRow;
  vencidas: number;
  andamento: number;
  prontas: number;
  isExec: boolean;
  items: PendingItem[];
}

export function Equipe() {
  const { currentOrgId, grants, areas } = useAuth();
  const { loading, products, template } = useOrgData(currentOrgId);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [managers, setManagers] = useState<Record<string, string | null>>({});
  const [loadingUsers, setLoadingUsers] = useState(true);

  const areaNameByKey = useMemo(
    () => Object.fromEntries(areas.map((a) => [a.key, a.name])),
    [areas],
  );

  useEffect(() => {
    if (!currentOrgId) return;
    setLoadingUsers(true);
    Promise.all([listUsers(currentOrgId), loadManagers(currentOrgId)])
      .then(([us, mg]) => {
        setUsers(us);
        setManagers(mg);
      })
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [currentOrgId]);

  const nameById = useMemo(
    () => Object.fromEntries(users.map((u) => [u.user_id, u.full_name ?? u.email])),
    [users],
  );

  const linhas: Linha[] = useMemo(() => {
    const today = new Date();
    return users.map((u) => {
      const isExec = u.role === 'editor';
      const rec: Recipient = {
        name: u.full_name ?? u.email,
        email: u.email,
        role: u.role,
        areaKeys: u.area_keys,
      };
      const items = isExec ? pendingFor(rec, products, template, today) : [];
      return {
        u,
        isExec,
        items,
        vencidas: items.filter((i) => i.atrasoDias > 0).length,
        andamento: items.filter((i) => i.kind === 'em_andamento').length,
        prontas: items.filter((i) => i.kind === 'pronta').length,
      };
    });
  }, [users, products, template]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExp = (id: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  if (!grants || !isAdminOrGestor(grants)) {
    return (
      <>
        <div className="topbar"><h1>Equipe</h1></div>
        <div className="content">
          <div className="card card-pad">
            <p className="muted">Apenas administradores e gestores veem a visão de equipe.</p>
          </div>
        </div>
      </>
    );
  }

  const totalVencidas = linhas.reduce((a, l) => a + l.vencidas, 0);

  return (
    <>
      <div className="topbar">
        <h1>Equipe</h1>
        <div className="stat-strip">
          <div className="stat-chip">
            <span className="n tabular">{linhas.filter((l) => l.isExec).length}</span>
            <span className="l">Executores</span>
          </div>
          <div className={`stat-chip ${totalVencidas ? 'warn' : ''}`}>
            <span className="n tabular">{totalVencidas}</span>
            <span className="l">Atividades em atraso</span>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="card card-pad">
          <div className="section-title">
            <h2>Nível de atendimento por pessoa</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              contagem de tarefas em atraso, em andamento e prontas para iniciar
            </span>
          </div>
          {loading || loadingUsers ? (
            <div className="center-note">Carregando…</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th className="l">Membro</th>
                    <th className="l">Perfil</th>
                    <th className="l">Áreas</th>
                    <th className="l">Superior</th>
                    <th>Em atraso</th>
                    <th>Em andamento</th>
                    <th>Prontas</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => {
                    const sup = managers[l.u.user_id];
                    const supName = sup ? nameById[sup] : null;
                    const canExpand = l.isExec && l.items.length > 0;
                    const isOpen = expanded.has(l.u.user_id);
                    return (
                      <Fragment key={l.u.user_id}>
                        <tr>
                          <td className="l" style={{ fontWeight: 600 }}>
                            {canExpand && (
                              <button
                                className="exp-btn"
                                title={isOpen ? 'Recolher' : 'Ver pendências'}
                                onClick={() => toggleExp(l.u.user_id)}
                              >
                                {isOpen ? '▾' : '▸'}
                              </button>
                            )}
                            {l.u.full_name ?? l.u.email}
                          </td>
                          <td className="l">{profileFor(l.u.role, l.u.area_keys)?.label ?? ROLE_LABELS[l.u.role]}</td>
                          <td className="l muted">
                            {l.u.role === 'editor'
                              ? l.u.area_keys.map((k) => areaNameByKey[k] ?? k).join(', ') || '—'
                              : 'todas'}
                          </td>
                          <td className="l muted">
                            {supName ? (
                              <span title="Superior é avisado quando há atrasos">↑ {supName}</span>
                            ) : (
                              '—'
                            )}
                          </td>
                          {l.isExec ? (
                            <>
                              <td className="tabular" style={{ color: l.vencidas ? 'var(--st-atrasada)' : 'inherit', fontWeight: l.vencidas ? 700 : 400 }}>{l.vencidas}</td>
                              <td className="tabular">{l.andamento}</td>
                              <td className="tabular">{l.prontas}</td>
                            </>
                          ) : (
                            <td colSpan={3} className="muted" style={{ textAlign: 'center' }}>gestão (vê tudo)</td>
                          )}
                        </tr>
                        {isOpen && (
                          <tr className="pend-detail">
                            <td colSpan={7}>
                              <div className="pend-list">
                                {l.items.map((it) => (
                                  <div className="pend-item" key={`${it.productId}-${it.n}`}>
                                    <b>{it.brand} · {it.productName}</b> — {it.phase} · {it.stageName}{' '}
                                    {it.atrasoDias > 0 ? (
                                      <span className="pd-late">(vencida há {it.atrasoDias} d)</span>
                                    ) : (
                                      <span className="muted">
                                        ({it.kind === 'em_andamento' ? 'em andamento' : 'pronta p/ iniciar'})
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {linhas.length === 0 && (
                    <tr><td colSpan={7} className="muted">Nenhum membro ainda.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
            O <b>superior</b> de cada membro é definido na tela de Usuários e será avisado por e-mail
            quando o liderado tiver atividades em atraso (envio automático a ativar).
          </p>
        </div>
      </div>
    </>
  );
}
